import type { DataProvider, Identifier } from "ra-core";

import type { Project, Task } from "../types";

export const REFERRAL_FOLLOW_UP_OFFSETS = [7, 30] as const;
const referralTaskPattern = /^Referral follow-up for .+ \((7|30) days\)$/;
const inFlightByProvider = new WeakMap<
  object,
  Map<Identifier, Promise<unknown[]>>
>();

const toDateOnly = (value: string | null | undefined, fallback: Date) => {
  if (value) {
    const parsed = new Date(`${value.slice(0, 10)}T00:00:00Z`);
    if (!Number.isNaN(parsed.valueOf())) return parsed;
  }
  return fallback;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
};

export type ReferralFollowUpTaskInput = {
  project_id: Identifier;
  contact_id?: Identifier | null;
  type: string;
  text: string;
  due_date: string;
  sales_id?: Identifier;
};

export const getReferralFollowUpTasks = (
  project: Pick<
    Project,
    | "id"
    | "name"
    | "primary_contact_id"
    | "contact_ids"
    | "job_date"
    | "reinstall_date"
    | "updated_at"
    | "sales_id"
  >,
  stageChangeDate = new Date(),
): ReferralFollowUpTaskInput[] => {
  // Completion dates drive the follow-up schedule. If neither completion
  // date is available, the caller's stage-change timestamp is the explicit
  // fallback; updated_at can reflect an unrelated edit.
  const completionDate = toDateOnly(
    project.job_date ?? project.reinstall_date,
    stageChangeDate,
  );
  const contactId =
    project.primary_contact_id ?? project.contact_ids?.[0] ?? null;
  return REFERRAL_FOLLOW_UP_OFFSETS.map((offset) => {
    const dueDate = addDays(completionDate, offset);
    return {
      project_id: project.id,
      contact_id: contactId,
      type: "follow-up",
      text: `Referral follow-up for ${project.name} (${offset} days)`,
      due_date: dueDate.toISOString(),
      sales_id: project.sales_id,
    };
  });
};

const createMissingReferralMiningTasks = async (
  dataProvider: DataProvider,
  project: Pick<
    Project,
    | "id"
    | "name"
    | "stage"
    | "primary_contact_id"
    | "contact_ids"
    | "job_date"
    | "reinstall_date"
    | "updated_at"
    | "sales_id"
  >,
  stageChangeDate = new Date(),
): Promise<unknown[]> => {
  const existing = await dataProvider.getList<Task>("tasks", {
    filter: { project_id: project.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "due_date", order: "ASC" },
  });
  const existingFollowUpOffsets = new Set(
    existing.data
      .map((task) => task.text?.match(referralTaskPattern)?.[1])
      .filter((offset): offset is string => offset != null),
  );
  const missing = getReferralFollowUpTasks(project, stageChangeDate).filter(
    (task) =>
      !existingFollowUpOffsets.has(
        task.text.match(referralTaskPattern)?.[1] ?? "",
      ),
  );
  return Promise.all(
    missing.map((task) => dataProvider.create("tasks", { data: task })),
  );
};

export const ensureReferralMiningTasks = (
  dataProvider: DataProvider,
  project: Parameters<typeof createMissingReferralMiningTasks>[1],
  stageChangeDate = new Date(),
) => {
  if (project.stage !== "referral-mining") return Promise.resolve([]);

  // Drag-and-drop and edit flows can both observe the same stage transition.
  // Share an in-flight read/create operation per provider and project so the
  // two callers cannot create duplicate 7/30-day tasks.
  const providerKey = dataProvider as object;
  let providerRequests = inFlightByProvider.get(providerKey);
  if (!providerRequests) {
    providerRequests = new Map();
    inFlightByProvider.set(providerKey, providerRequests);
  }
  const existingRequest = providerRequests.get(project.id);
  if (existingRequest) return existingRequest;

  const request = createMissingReferralMiningTasks(
    dataProvider,
    project,
    stageChangeDate,
  );
  providerRequests.set(project.id, request);
  void request.then(
    () => {
      if (providerRequests?.get(project.id) === request) {
        providerRequests.delete(project.id);
      }
    },
    () => {
      if (providerRequests?.get(project.id) === request) {
        providerRequests.delete(project.id);
      }
    },
  );
  return request;
};
